import { AuthApi } from '@up2stay/up2promo-api-client';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  cn,
  healthOkFgClass,
  healthOkIconSoftClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import type { AxiosError } from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { SETTINGS_SECTION_DESCRIPTION_CLASS } from '@/core/components/settings/settings-section';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { AppIonPage } from '@/core/components/app-ion-page';

enum ConfirmationStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

/** Shell navigation needs a path; API may return an absolute frontend URL. */
function toShellRedirectPath(urlOrPath: string | null | undefined): string {
  const fallback = shellPaths.moreUserSettings;
  if (!urlOrPath) {
    return fallback;
  }

  try {
    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
      const url = new URL(urlOrPath);
      const path = `${url.pathname}${url.search}`;
      return path.startsWith('/tabs') ? path : fallback;
    }
  } catch {
    return fallback;
  }

  return urlOrPath.startsWith('/tabs') ? urlOrPath : fallback;
}

function ConfirmEmailBreadcrumb({
  domainLabel,
  settingsLabel,
}: {
  domainLabel: string;
  settingsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {settingsLabel}
    </>
  );
}

export function ConfirmNewEmailPage() {
  const dict = useDictionary();
  const settingsDict = dict.settings;
  const { navigateShell } = useNavigateShell();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const setUser = useAuthStore((state) => state.setUser);
  const getErrorMessage = useErrorMessage();

  const hash = searchParams.get('hash') ?? null;
  const redirect = searchParams.get('redirectUrl') ?? null;

  const [status, setStatus] = useState<ConfirmationStatus>(() => {
    if (!hash) {
      return ConfirmationStatus.ERROR;
    }
    return ConfirmationStatus.LOADING;
  });
  const [error, setError] = useState<string | null>(() => {
    if (!hash) {
      return getErrorMessage('invalidHash');
    }
    return null;
  });
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hash) {
      return;
    }

    const confirmEmail = async () => {
      try {
        const response = await AuthApi.confirmNewEmail({
          body: {
            hash,
          },
          headers: {
            origin: typeof window !== 'undefined' ? window.location.origin : '',
            referer: typeof window !== 'undefined' ? window.location.href : '',
          },
        });

        // Refresh user data if user is authenticated
        try {
          const userResponse = await AuthApi.me();
          if (userResponse.data) {
            setUser(userResponse.data);
          }
        } catch {
          // User might not be authenticated, that's okay
          // The email confirmation still works
        }

        setStatus(ConfirmationStatus.SUCCESS);

        // Prefer query redirect (from the mail link), then API redirect, then settings.
        const responseData = response.data as
          | { redirectUrl?: string }
          | undefined;
        const finalRedirectUrl = toShellRedirectPath(
          redirect ?? responseData?.redirectUrl,
        );
        setRedirectUrl(finalRedirectUrl);

        // Redirect after a short delay
        setTimeout(() => {
          navigateShell(finalRedirectUrl);
        }, 2000);
      } catch (err) {
        const error = err as AxiosError<{ errors?: Record<string, string> }>;
        setStatus(ConfirmationStatus.ERROR);
        const backendErrors = error.response?.data?.errors;
        const firstCode =
          (backendErrors &&
            (Object.values(backendErrors)[0] as string | undefined)) ||
          undefined;

        setError(getErrorMessage(firstCode));
      }
    };

    void confirmEmail();
  }, [hash, redirect, navigateShell, setUser, getErrorMessage]);

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            breadcrumb={
              <ConfirmEmailBreadcrumb
                domainLabel={dict.nav.account}
                settingsLabel={dict.nav.settings}
              />
            }
            title={settingsDict.contact.confirmEmailTitle}
            backHref={shellPaths.moreUserSettings}
          />

          <Card className="mx-auto w-full max-w-md overflow-hidden">
            <CardContent className="flex flex-col gap-5 p-5 sm:p-6">
              {status === ConfirmationStatus.LOADING && (
                <div className="flex flex-col items-center gap-4 py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className={SETTINGS_SECTION_DESCRIPTION_CLASS}>
                    {settingsDict.contact.confirmingEmail}
                  </p>
                </div>
              )}

              {status === ConfirmationStatus.SUCCESS && (
                <div className="flex flex-col items-center gap-4">
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-2xl',
                      healthOkIconSoftClass,
                    )}
                  >
                    <CheckCircle2 className="h-6 w-6" aria-hidden />
                  </div>
                  <Alert className={cn(healthOkSurfaceClass)}>
                    <AlertDescription className={healthOkFgClass}>
                      {settingsDict.contact.emailConfirmed}
                    </AlertDescription>
                  </Alert>
                  {redirectUrl ? (
                    <p className={SETTINGS_SECTION_DESCRIPTION_CLASS}>
                      {settingsDict.contact.redirecting}
                    </p>
                  ) : null}
                </div>
              )}

              {status === ConfirmationStatus.ERROR && (
                <div className="flex flex-col items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                    <XCircle className="h-6 w-6" aria-hidden />
                  </div>
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                  <Button
                    variant="outline"
                    onClick={() => navigateShell(shellPaths.moreUserSettings)}
                  >
                    {settingsDict.contact.goToSettings}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
